import DateInput from '../../../components/DateInput';
import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../../lib/api';
import { DISCIPLINES } from '../../../lib/disciplines';
import { WORLD_CURRENCIES } from '../../../lib/currencies';
import { Camera, Landmark, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Modal, Section, L } from './ui';
import { TaxPanel } from './TaxPanel';

const EMPTY = {
  first_name: '', last_name: '', email: '', phone: '', photo_url: '',
  staff_type: 'clinician', outsourced_company: '', hire_type: 'employment_contract',
  professional_title: '', discipline: '', professional_license_no: '',
  malpractice_insurer: '', malpractice_policy_no: '', malpractice_expiry: '',
  bio_cv: '', cv_link: '', schedule_type: 'full_time', weekly_hours: 40,
  hourly_wage: '', monthly_salary: '', currency: 'PLN', tax_country: 'PL',
  payment_form: 'bank_transfer', perks: [], vacation_days: 20,
  holiday_pay_extra_pct: 100, additional_uniform_fee: '', custom_perks: '',
  date_hired: '', date_separated: '', separation_type: '',
  hiring_manager_user_id: '', hiring_manager_name: '',
  early_termination_penalty: "one (1) month's gross remuneration",
  notice_period_days: 30, notes: '',
  bank_full_legal_name: '', bank_name_branch: '', bank_region: '',
  bank_iban: '', bank_bic: '', bank_sort_code: '', bank_account_number: '',
  bank_routing_number: '', bank_transit_number: '',
  salary_net_amount: '', payment_reference: '',
  vacation_start: '', vacation_end: '', extra_vacation_dates: '',
  sick_dates: '', bereavement_dates: '', maternity_start: '', maternity_end: '',
};

const BANK_REGIONS = [
  { key: 'sepa', label: 'Europe (SEPA) — IBAN + BIC/SWIFT' },
  { key: 'uk', label: 'UK — Sort Code + Account Number' },
  { key: 'us_ca', label: 'USA/Canada — Routing, Transit + Account Number' },
];

function suggestRegion(country) {
  const c = (country || '').toUpperCase();
  if (c === 'GB' || c === 'UK') return 'uk';
  if (c === 'US' || c === 'CA') return 'us_ca';
  return 'sepa';
}

function defaultPayRef() {
  const d = new Date();
  return `Salary ${d.toLocaleString('en', { month: 'long' })} ${d.getFullYear()}`;
}

function buildPayload(f) {
  const payload = { ...f,
    weekly_hours: f.weekly_hours === '' ? null : parseFloat(f.weekly_hours),
    hourly_wage: f.hourly_wage === '' ? null : parseFloat(f.hourly_wage),
    monthly_salary: f.monthly_salary === '' ? null : parseFloat(f.monthly_salary),
    salary_net_amount: f.salary_net_amount === '' || f.salary_net_amount == null ? null : parseFloat(f.salary_net_amount),
    vacation_days: f.vacation_days === '' ? null : parseInt(f.vacation_days, 10),
    holiday_pay_extra_pct: f.holiday_pay_extra_pct === '' ? null : parseFloat(f.holiday_pay_extra_pct),
    additional_uniform_fee: f.additional_uniform_fee === '' ? null : parseFloat(f.additional_uniform_fee),
    notice_period_days: f.notice_period_days === '' ? 30 : parseInt(f.notice_period_days, 10),
  };
  delete payload.employee_id; delete payload.clinic_id; delete payload.signatures;
  delete payload.linked_user_id; delete payload.created_by; delete payload.created_at; delete payload.updated_at;
  return payload;
}

/* ---------------- Hiring form ---------------- */

function HRForm({ initial, meta, admins, banks, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, payment_reference: initial.payment_reference || defaultPayRef() });
  const [busy, setBusy] = useState(false);
  const [autosaved, setAutosaved] = useState('');
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const isEdit = !!initial.employee_id;
  const dirty = useRef(false);
  const firstRender = useRef(true);

  // Autosave — edit mode only, debounced 2s after the last change.
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (!isEdit) return;
    dirty.current = true;
    const t = setTimeout(async () => {
      if (!dirty.current || !f.first_name.trim() || !f.last_name.trim()) return;
      try {
        await api.put(`/hr/employees/${initial.employee_id}`, buildPayload(f));
        dirty.current = false;
        setAutosaved(new Date().toLocaleTimeString());
      } catch { /* silent — manual save surfaces errors */ }
    }, 2000);
    return () => clearTimeout(t);
  }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!f.first_name.trim() || !f.last_name.trim()) { toast.error('First and last name are required.'); return; }
    setBusy(true);
    try {
      if (isEdit) await api.put(`/hr/employees/${initial.employee_id}`, buildPayload(f));
      else await api.post('/hr/employees', buildPayload(f));
      toast.success('HR record saved.');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const uploadPhoto = (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    if (file.size > 1024 * 1024) { toast.error('Keep the photo under 1 MB.'); return; }
    const r = new FileReader();
    r.onload = e2 => set('photo_url', e2.target.result);
    r.readAsDataURL(file);
  };

  const perkOn = (p) => (f.perks || []).includes(p);
  const togglePerk = (p) => set('perks', perkOn(p) ? f.perks.filter(x => x !== p) : [...(f.perks || []), p]);

  const showBanking = f.payment_form === 'bank_transfer' || f.payment_form === 'credit_card_deposit';
  const onBankName = (v) => {
    setF(x => ({ ...x, bank_name_branch: v,
      bank_region: x.bank_region || (v ? suggestRegion(x.tax_country) : x.bank_region) }));
  };

  return (
    <Modal onClose={onClose} wide>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="font-heading font-bold text-xl mb-1">{isEdit ? 'Edit HR record' : 'New hiring package'}</h2>
          <p className="text-xs text-fg-muted mb-4">Standard written-terms package (identity, role, remuneration, insurance, perks, termination) aligned with common international employment standards.</p>
        </div>
        {isEdit && autosaved && (
          <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1 shrink-0 mr-6" data-testid="hr-autosaved">
            <Check className="w-3 h-3"/> Autosaved {autosaved}
          </span>
        )}
      </div>

      <Section title="Identity">
        <div className="flex items-center gap-4 mb-3">
          <div className="w-16 h-16 rounded-full bg-[#F2EBDB] overflow-hidden flex items-center justify-center">
            {f.photo_url ? <img src={f.photo_url} alt="" className="w-full h-full object-cover"/> : <Camera className="w-5 h-5 text-fg-muted"/>}
          </div>
          <label className="btn-secondary text-xs cursor-pointer">Upload photo
            <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="hr-photo-input"/>
          </label>
          {f.photo_url && <button className="text-xs text-[#B83A3A]" onClick={() => set('photo_url', '')}>Remove</button>}
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <L label="First name *"><input className="input" value={f.first_name} onChange={e=>set('first_name', e.target.value)} data-testid="hr-first-name"/></L>
          <L label="Last name *"><input className="input" value={f.last_name} onChange={e=>set('last_name', e.target.value)} data-testid="hr-last-name"/></L>
          <L label="Email (used to create their login)"><input className="input" type="email" value={f.email || ''} onChange={e=>set('email', e.target.value)} data-testid="hr-email"/></L>
          <L label="Phone"><input className="input" value={f.phone || ''} onChange={e=>set('phone', e.target.value)}/></L>
        </div>
      </Section>

      <Section title="Role & credentials">
        <div className="grid md:grid-cols-2 gap-3">
          <L label="Staff type">
            <select className="input" value={f.staff_type} onChange={e=>set('staff_type', e.target.value)} data-testid="hr-staff-type">
              {(meta?.staff_types || []).map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
            </select>
          </L>
          {(f.staff_type === 'cleaning' || f.staff_type === 'maintenance') && (
            <L label="Outsourced company (if a company provides the service)">
              <input className="input" value={f.outsourced_company || ''} onChange={e=>set('outsourced_company', e.target.value)} placeholder="e.g. CleanPro Sp. z o.o." data-testid="hr-company"/>
            </L>
          )}
          <L label="Type of hiring">
            <select className="input" value={f.hire_type} onChange={e=>set('hire_type', e.target.value)} data-testid="hr-hire-type">
              {(meta?.hire_types || []).map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
            </select>
          </L>
          <L label="Professional title">
            <select className="input" value={f.professional_title || ''} onChange={e=>set('professional_title', e.target.value)} data-testid="hr-title">
              <option value="">— Select —</option>
              {(meta?.professional_titles || []).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </L>
          <L label="Discipline">
            <select className="input" value={f.discipline || ''} onChange={e=>set('discipline', e.target.value)} data-testid="hr-discipline">
              <option value="">— Select —</option>
              {DISCIPLINES.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </L>
          <L label="Professional license #"><input className="input font-mono" value={f.professional_license_no || ''} onChange={e=>set('professional_license_no', e.target.value)} data-testid="hr-license"/></L>
          <L label="Malpractice insurer"><input className="input" value={f.malpractice_insurer || ''} onChange={e=>set('malpractice_insurer', e.target.value)} data-testid="hr-insurer"/></L>
          <L label="Malpractice policy #"><input className="input font-mono" value={f.malpractice_policy_no || ''} onChange={e=>set('malpractice_policy_no', e.target.value)}/></L>
          <L label="Malpractice valid until"><DateInput value={f.malpractice_expiry || ''} onChange={e=>set('malpractice_expiry', e.target.value)}/></L>
          <L label="CV link"><input className="input" value={f.cv_link || ''} onChange={e=>set('cv_link', e.target.value)} placeholder="https://…"/></L>
        </div>
        <L label="Bio / CV summary" full>
          <textarea className="input min-h-[70px]" value={f.bio_cv || ''} onChange={e=>set('bio_cv', e.target.value)} data-testid="hr-bio"/>
        </L>
      </Section>

      <Section title="Schedule & pay">
        <div className="grid md:grid-cols-3 gap-3">
          <L label="Schedule">
            <select className="input" value={f.schedule_type} onChange={e=>set('schedule_type', e.target.value)} data-testid="hr-schedule">
              <option value="full_time">Full-time</option>
              <option value="part_time">Part-time</option>
            </select>
          </L>
          <L label="Total hours / week"><input className="input" type="number" min="1" max="60" value={f.weekly_hours ?? ''} onChange={e=>set('weekly_hours', e.target.value)} data-testid="hr-hours"/></L>
          <L label="Currency">
            <select className="input" value={f.currency} onChange={e=>set('currency', e.target.value)}>
              {WORLD_CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </L>
          <L label="Hourly wage (gross)"><input className="input" type="number" min="0" step="0.5" value={f.hourly_wage ?? ''} onChange={e=>set('hourly_wage', e.target.value)} data-testid="hr-hourly"/></L>
          <L label="Monthly salary (gross)"><input className="input" type="number" min="0" step="50" value={f.monthly_salary ?? ''} onChange={e=>set('monthly_salary', e.target.value)} data-testid="hr-salary"/></L>
          <L label="Form of payment">
            <select className="input" value={f.payment_form} onChange={e=>set('payment_form', e.target.value)} data-testid="hr-payment-form">
              {(meta?.payment_forms || []).map(p => <option key={p} value={p}>{p.replace(/_/g,' ')}</option>)}
            </select>
          </L>
          <L label="Tax country (clinic's country by default)"><input className="input uppercase" maxLength={2} value={f.tax_country} onChange={e=>set('tax_country', e.target.value.toUpperCase())} data-testid="hr-tax-country"/></L>
        </div>
        <TaxPanel f={f}/>
      </Section>

      {showBanking && (
        <Section title="Banking & payment delivery">
          <div className="flex items-center gap-1.5 text-xs text-fg-muted mb-3">
            <Landmark className="w-3.5 h-3.5 text-brand"/> Deposit / transfer details for salary payments. Bank names you enter are saved and reusable across staff.
          </div>
          <p className="text-[10px] text-fg-muted mb-3 -mt-2">Banking data is stored under GDPR Art. 6(1)(b) (contract performance), visible to admins only. Verify the IBAN/account with the staff member in writing before the first transfer.</p>
          <div className="grid md:grid-cols-2 gap-3">
            <L label="Full legal name (account holder)">
              <input className="input" value={f.bank_full_legal_name || ''} onChange={e=>set('bank_full_legal_name', e.target.value)} data-testid="hr-bank-legal-name"/>
            </L>
            <L label="Bank name and branch">
              <input className="input" list="hr-bank-list" value={f.bank_name_branch || ''}
                     onChange={e=>onBankName(e.target.value)} placeholder="e.g. PKO BP — Warszawa Centrum" data-testid="hr-bank-name"/>
              <datalist id="hr-bank-list">
                {(banks || []).map(b => <option key={b} value={b}/>)}
              </datalist>
            </L>
            <L label="Account identifier (region)">
              <select className="input" value={f.bank_region || ''} onChange={e=>set('bank_region', e.target.value)} data-testid="hr-bank-region">
                <option value="">— Select —</option>
                {BANK_REGIONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </L>
            <L label="Payment reference">
              <input className="input" value={f.payment_reference || ''} onChange={e=>set('payment_reference', e.target.value)} placeholder={defaultPayRef()} data-testid="hr-payment-reference"/>
            </L>
            {f.bank_region === 'sepa' && (<>
              <L label="IBAN"><input className="input font-mono" value={f.bank_iban || ''} onChange={e=>set('bank_iban', e.target.value.toUpperCase())} placeholder="PL61 1090 1014 0000 0712 1981 2874" data-testid="hr-bank-iban"/></L>
              <L label="BIC / SWIFT code"><input className="input font-mono" value={f.bank_bic || ''} onChange={e=>set('bank_bic', e.target.value.toUpperCase())} placeholder="WBKPPLPP" data-testid="hr-bank-bic"/></L>
            </>)}
            {f.bank_region === 'uk' && (<>
              <L label="Sort Code"><input className="input font-mono" value={f.bank_sort_code || ''} onChange={e=>set('bank_sort_code', e.target.value)} placeholder="04-00-04" data-testid="hr-bank-sort"/></L>
              <L label="Account Number"><input className="input font-mono" value={f.bank_account_number || ''} onChange={e=>set('bank_account_number', e.target.value)} data-testid="hr-bank-account"/></L>
            </>)}
            {f.bank_region === 'us_ca' && (<>
              <L label="Routing Number"><input className="input font-mono" value={f.bank_routing_number || ''} onChange={e=>set('bank_routing_number', e.target.value)} data-testid="hr-bank-routing"/></L>
              <L label="Transit Number"><input className="input font-mono" value={f.bank_transit_number || ''} onChange={e=>set('bank_transit_number', e.target.value)} data-testid="hr-bank-transit"/></L>
              <L label="Account Number"><input className="input font-mono" value={f.bank_account_number || ''} onChange={e=>set('bank_account_number', e.target.value)} data-testid="hr-bank-account"/></L>
            </>)}
            <L label="Salary amount — exact net pay (after all legal deductions & taxes)">
              <input className="input" type="number" min="0" step="0.01" value={f.salary_net_amount ?? ''} onChange={e=>set('salary_net_amount', e.target.value)} data-testid="hr-net-salary"/>
            </L>
          </div>
        </Section>
      )}

      <Section title="Perks & benefits">
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(meta?.perk_options || []).map(p => (
            <button key={p} type="button" onClick={() => togglePerk(p)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${perkOn(p) ? 'bg-brand text-white border-brand' : 'surface border-soft text-fg-secondary hover:border-brand/50'}`}
                    data-testid={`hr-perk-${p}`}>
              {perkOn(p) ? '✓ ' : ''}{p.replace(/_/g,' ')}
            </button>
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <L label="Paid vacation (days/year)"><input className="input" type="number" min="0" value={f.vacation_days ?? ''} onChange={e=>set('vacation_days', e.target.value)} data-testid="hr-vacation"/></L>
          <L label="Holiday work extra pay (%)"><input className="input" type="number" min="0" value={f.holiday_pay_extra_pct ?? ''} onChange={e=>set('holiday_pay_extra_pct', e.target.value)} data-testid="hr-holiday-pct"/></L>
          <L label="Additional uniform fee (first is free)"><input className="input" type="number" min="0" value={f.additional_uniform_fee ?? ''} onChange={e=>set('additional_uniform_fee', e.target.value)} data-testid="hr-uniform-fee"/></L>
        </div>
        <L label="Other perks (free data entry)" full><input className="input" value={f.custom_perks || ''} onChange={e=>set('custom_perks', e.target.value)} placeholder="e.g. gym access, conference budget…" data-testid="hr-custom-perks"/></L>
      </Section>

      <Section title="Dates, hiring manager & termination terms">
        <div className="grid md:grid-cols-3 gap-3">
          <L label="Date hired"><DateInput value={f.date_hired || ''} onChange={e=>set('date_hired', e.target.value)} data-testid="hr-date-hired"/></L>
          <L label="Date terminated / quit"><DateInput value={f.date_separated || ''} onChange={e=>set('date_separated', e.target.value)} data-testid="hr-date-separated"/></L>
          <L label="Separation type">
            <select className="input" value={f.separation_type || ''} onChange={e=>set('separation_type', e.target.value)} data-testid="hr-separation-type">
              <option value="">— still employed —</option>
              {(meta?.separation_types || []).filter(Boolean).map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
            </select>
          </L>
          <L label="Person responsible for hiring">
            <select className="input" value={f.hiring_manager_user_id || ''}
                    onChange={e => {
                      const u = admins.find(a => a.user_id === e.target.value);
                      setF(x => ({ ...x, hiring_manager_user_id: e.target.value, hiring_manager_name: u?.name || '' }));
                    }} data-testid="hr-hiring-manager">
              <option value="">— Select admin —</option>
              {admins.map(a => <option key={a.user_id} value={a.user_id}>{`${a.name} (${a.email})`}</option>)}
            </select>
          </L>
          <L label="Notice period (days)"><input className="input" type="number" min="0" value={f.notice_period_days ?? 30} onChange={e=>set('notice_period_days', e.target.value)}/></L>
        </div>
        <L label="Early-termination penalty" full>
          <textarea className="input min-h-[64px] !text-sm" rows={3}
                    value={f.early_termination_penalty || ''}
                    onChange={e=>set('early_termination_penalty', e.target.value)}
                    placeholder="e.g. one (1) month's gross remuneration, plus reimbursement of any training costs incurred within the last 6 months…"
                    data-testid="hr-penalty"/>
        </L>
        <L label="Internal notes (never printed)" full><textarea className="input min-h-[50px]" value={f.notes || ''} onChange={e=>set('notes', e.target.value)}/></L>
      </Section>

      <div className="flex gap-2 justify-end mt-4">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy} onClick={save} data-testid="hr-save-btn">{busy ? 'Saving…' : 'Save HR record'}</button>
      </div>
    </Modal>
  );
}

export { HRForm, EMPTY, BANK_REGIONS, suggestRegion, defaultPayRef, buildPayload };
